import * as Sentry from '@sentry/react'
import { isAxiosError } from 'axios'
import { RotateCcw } from 'lucide-react'
import { createElement } from 'react'
import { toast } from 'sonner'
import { ContractError } from '@/shared/api/assert-shape'
import { ErrorToast } from '@/shared/components/error-toast'
import { ERROR_MESSAGES } from '@/shared/constants/errors/messages'
import { ERROR_PRESENTATION } from '@/shared/constants/errors/presentation'
import type { ApiErrorResponse } from '@/shared/types/api-error'
import type { ErrorAction, ErrorSeverity } from '@/shared/types/error-action'

type ProblemDetails = {
  status?: unknown
  code?: unknown
  detail?: unknown
  title?: unknown
  instance?: unknown
  requestId?: unknown
  errors?: unknown
}

function normalizeGenericUnauthorized(error: ApiErrorResponse): ApiErrorResponse {
  const genericUnauthorized = [error.errorCode, error.message].some(
    (value) =>
      typeof value === 'string' &&
      /^(unauthorized|unauthorizedexception|invalid access token)$/i.test(value.trim())
  )

  return error.statusCode === 401 && genericUnauthorized
    ? { ...error, errorCode: 'ACCESS_TOKEN_INVALID' }
    : error
}

function extractProblemDetails(value: unknown): ApiErrorResponse | null {
  if (!value || typeof value !== 'object') return null

  const problem = value as ProblemDetails
  if (typeof problem.status !== 'number') return null
  if (
    typeof problem.code !== 'string' &&
    typeof problem.detail !== 'string' &&
    typeof problem.title !== 'string'
  ) {
    return null
  }

  return normalizeGenericUnauthorized({
    statusCode: problem.status,
    errorCode: typeof problem.code === 'string' ? problem.code : 'ERROR_REQUEST',
    message:
      typeof problem.detail === 'string'
        ? problem.detail
        : typeof problem.title === 'string'
          ? problem.title
          : 'Error',
    timestamp: new Date().toISOString(),
    path: typeof problem.instance === 'string' ? problem.instance : '',
    method: '',
    requestId: typeof problem.requestId === 'string' ? problem.requestId : undefined,
    details: problem.errors
  })
}

function isFetchNetworkError(error: unknown): error is TypeError {
  return (
    error instanceof TypeError &&
    /failed to fetch|networkerror|network request failed|load failed/i.test(error.message)
  )
}

/**
 * Extracts the canonical API error from either Axios' legacy envelope or the
 * RFC 9457 Problem Details object thrown by the generated v2 fetch client.
 */
export function extractApiError(error: unknown): ApiErrorResponse | null {
  if (!isAxiosError(error)) return extractProblemDetails(error)
  if (!error.response?.data) return null

  const data = error.response.data

  const problem = extractProblemDetails(data)
  if (problem) {
    return {
      ...problem,
      path: problem.path || error.config?.url || '',
      method: error.config?.method?.toUpperCase() ?? ''
    }
  }

  // Standard format from GlobalExceptionFilter
  if (data.errorCode && data.statusCode) {
    return normalizeGenericUnauthorized(data as ApiErrorResponse)
  }

  // Legacy format: { message: string, statusCode: number }
  if (data.message && data.statusCode) {
    return normalizeGenericUnauthorized({
      statusCode: data.statusCode,
      errorCode: typeof data.message === 'string' ? data.message : 'ERROR_REQUEST',
      message: typeof data.message === 'string' ? data.message : 'Error',
      timestamp: new Date().toISOString(),
      path: error.config?.url ?? '',
      method: error.config?.method?.toUpperCase() ?? ''
    })
  }

  return null
}

/**
 * Machine error codes are SCREAMING_SNAKE_CASE tokens with no spaces
 * (e.g. 'RENAP_ERROR', 'CLIENT_DUPLICATE'). Human messages read as sentences and
 * are safe to show as-is. Used to decide whether a server `message` is a real
 * message or just the raw errorCode echoed back.
 */
function looksLikeErrorCode(value: string): boolean {
  return /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/.test(value)
}

/**
 * Códigos CAJÓN DE SASTRE: no describen NADA. Son el relleno que pone el servidor
 * cuando la excepción no trajo un código estable, no un diagnóstico.
 *
 * ☠️ SU TRADUCCIÓN LE COMÍA AL USUARIO EL ÚNICO TEXTO QUE EXPLICABA EL FALLO.
 *
 * En v2 el `GlobalExceptionFilter` aplana toda `HttpException` 4xx sin código propio a
 * `ERROR_REQUEST` y manda la frase real en `detail`. Como `ERROR_REQUEST` SÍ tiene entrada
 * en `ERROR_MESSAGES`, ganaba en la prioridad 1 y la frase moría acá. El 2026-08-24, pedir
 * correcciones sin marcar ningún campo mostró «Ocurrió un error al realizar la solicitud,
 * intente nuevamente más tarde» —o sea: reintentá, que capaz anda— cuando el servidor había
 * dicho exactamente qué faltaba hacer. El texto útil sólo se leyó entrando a los logs de
 * Railway.
 *
 * Por eso para estos códigos el orden se INVIERTE: la frase del servidor gana, y la
 * traducción queda de último recurso. No se agregan códigos que sí significan algo
 * (`NOT_FOUND`, `INTERNAL_ERROR`): su traducción es mejor que el `detail`, que en esos
 * casos suele venir en inglés (`'Negotiation not found'`).
 */
const PLACEHOLDER_ERROR_CODES = new Set(['ERROR_REQUEST'])

/**
 * Frases del servidor que no dicen más que el genérico — el relleno del propio filtro
 * cuando la excepción no trajo mensaje. Se tratan como si no hubieran llegado, así un
 * código cajón de sastre no cambia un texto en español por uno en inglés igual de mudo.
 */
const UNINFORMATIVE_SERVER_MESSAGES = new Set([
  'An error occurred',
  'An unexpected error occurred',
  'Error'
])

/**
 * Returns a user-friendly error message in Spanish.
 * Maps backend error codes to translated messages via ERROR_MESSAGES.
 */
export function getErrorMessage(error: unknown): string {
  // El portero del contrato ya escribió el diagnóstico —qué lectura, qué campo— y es lo
  // ÚNICO que hay: el servidor respondió 200, así que no hay `errorCode` que traducir ni
  // línea en los logs que buscar. Ver `assert-shape.ts`.
  if (error instanceof ContractError) return error.message

  const apiError = extractApiError(error)

  if (apiError) {
    // El mensaje del servidor sirve cuando se lee como una frase — NO cuando es un código
    // de máquina echado de vuelta (e.g. 'RENAP_ERROR'). Las excepciones de NestJS lanzadas
    // con un string plano dejan `message === errorCode`, así que sin esta guarda la
    // pantalla mostraría el código en la cara del usuario.
    const serverSentence =
      apiError.message &&
      !looksLikeErrorCode(apiError.message) &&
      !UNINFORMATIVE_SERVER_MESSAGES.has(apiError.message.trim())
        ? apiError.message
        : undefined

    // 1. Un código sin significado NO le gana a una frase concreta del servidor.
    if (serverSentence && PLACEHOLDER_ERROR_CODES.has(apiError.errorCode)) return serverSentence
    // 2. Traducción del front por `errorCode` estable (permite i18n / override).
    const translated = ERROR_MESSAGES[apiError.errorCode]
    if (translated) return translated
    // 3. La frase del servidor, que carga el contexto dinámico —qué código ya existe, qué
    //    campo falló— que ninguna traducción fija puede tener.
    if (serverSentence) return serverSentence
  }

  // Network error (no response from server)
  if ((isAxiosError(error) && !error.response) || isFetchNetworkError(error)) {
    return 'No pudimos comunicarnos con el servidor. Revisa tu conexión e intenta nuevamente. Si estás trabajando en local, confirma que la API esté en ejecución.'
  }

  return ERROR_MESSAGES.GENERIC_ERROR
}

export type ErrorPresentation = {
  severity: ErrorSeverity
  title?: string
  message: string
  requestId?: string
  fields?: string[]
}

/**
 * Los campos que el servidor NOMBRA al rechazar un guardado.
 *
 * ☠️ ESTE DATO YA LLEGABA Y SE TIRABA, Y ESO COSTÓ UN DÍA DE PLANTA COMERCIAL PARADA.
 *
 * `GlobalExceptionFilter` manda en `errors` la lista `{ field, message, kind }` de todo lo que
 * reprobó. `extractApiError` la venía guardando en `details`… y `getErrorPresentation` devolvía
 * sólo el mensaje traducido, así que la lista moría ahí. El 2026-08-14 el alta de una
 * negociación con cuotas rompía por un campo concreto y la pantalla decía «Error de validación
 * en la base de datos» a secas: el reporte llegó por WhatsApp con una captura, y el campo hubo
 * que encontrarlo leyendo el código.
 *
 * Va en la línea técnica junto a la referencia, no dentro del mensaje: el mensaje es para la
 * persona, la referencia y los campos son para quien recibe el reporte. La ruta viaja como la
 * manda el servidor —`paymentAgreement.payments.0.id`— porque traducirla sería inventar un
 * diccionario que se desincroniza con el schema al primer cambio.
 *
 * ☠️ Y HAY DOS FORMAS, NO UNA. Mongo manda objetos `{ field, message, kind }`; la validación Zod
 * de v2 manda STRINGS `"applicant.residence.zone: Expected integer…"`. Sólo se leían los objetos,
 * así que el 2026-10-02 el alta de un cliente rebotó seis veces con un 400 y el toast no nombró
 * ningún campo. Del string se toma la ruta antes del primer `": "`; un string sin ruta —los de
 * class-validator, `"name must be a string"`— no nombra un campo y se ignora.
 */
function failedFields(details: unknown): string[] | undefined {
  if (!Array.isArray(details)) return undefined

  const fields = details
    .map((entry) => {
      if (typeof entry === 'string') return pathOfIssue(entry)
      return entry && typeof entry === 'object' ? (entry as { field?: unknown }).field : null
    })
    .filter((field): field is string => typeof field === 'string' && field.length > 0)

  // La misma ruta puede reprobar dos reglas (p.ej. formato y longitud): se nombra una vez.
  const unique = [...new Set(fields)]
  return unique.length > 0 ? unique : undefined
}

/** Segmentos de una ruta Zod unidos por punto: `applicant.residence.zone`, `contacts.0.email`. */
const ISSUE_PATH = /^[A-Za-z0-9_$]+(?:\.[A-Za-z0-9_$]+)*$/

function pathOfIssue(issue: string): string | null {
  const separator = issue.indexOf(': ')
  if (separator <= 0) return null
  const path = issue.slice(0, separator)
  return ISSUE_PATH.test(path) ? path : null
}

/**
 * Resolves how an error should be presented: message + severity + optional title.
 * Severity defaults to the HTTP status (5xx/network → error, 4xx → warning) and
 * can be overridden per code via ERROR_PRESENTATION.
 */
export function getErrorPresentation(error: unknown): ErrorPresentation {
  const message = getErrorMessage(error)
  const apiError = extractApiError(error)
  const override = apiError ? ERROR_PRESENTATION[apiError.errorCode] : undefined

  const status = apiError?.statusCode ?? (isAxiosError(error) ? error.response?.status : undefined)
  const defaultSeverity: ErrorSeverity = !status || status >= 500 ? 'error' : 'warning'

  return {
    severity: override?.severity ?? defaultSeverity,
    title: override?.title,
    message,
    requestId: apiError?.requestId,
    fields: failedFields(apiError?.details)
  }
}

/**
 * Returns true if the error should be retried (5xx or network errors).
 * Client errors (4xx) should NOT be retried.
 */
export function isRetryableError(error: unknown): boolean {
  const apiError = extractApiError(error)
  if (apiError) return apiError.statusCode >= 500

  if (isFetchNetworkError(error)) return true
  if (!isAxiosError(error)) return false

  // Network error — no response received
  if (!error.response) return true

  // Only retry server errors (5xx)
  return error.response.status >= 500
}

/**
 * Reports an error to Sentry with the right severity:
 * - 5xx and network errors → captureException (level=error)
 * - 4xx (except 401 / VALIDATION_ERROR) → captureMessage (level=warning)
 * Pure side-effect, no UI. Shared by the global handler and the hook.
 */
export function reportErrorToSentry(error: unknown): void {
  const apiError = extractApiError(error)
  const status = apiError?.statusCode ?? (isAxiosError(error) ? error.response?.status : undefined)

  if (isFetchNetworkError(error)) {
    Sentry.withScope((scope) => {
      scope.setLevel('error')
      scope.setTag('kind', 'network')
      Sentry.captureException(error)
    })
    return
  }

  if (!isAxiosError(error) && !apiError) return

  // No response: network error / CORS / timeout.
  if (isAxiosError(error) && !error.response) {
    Sentry.withScope((scope) => {
      scope.setLevel('error')
      scope.setTag('kind', 'network')
      if (error.config?.url) scope.setExtra('path', error.config.url)
      Sentry.captureException(error)
    })
    return
  }

  if (status && status >= 500) {
    Sentry.withScope((scope) => {
      scope.setLevel('error')
      if (apiError) {
        scope.setTag('errorCode', apiError.errorCode)
        scope.setExtra('path', apiError.path)
        scope.setExtra('method', apiError.method)
        if (apiError.requestId) scope.setExtra('requestId', apiError.requestId)
      }
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error(`${apiError?.errorCode ?? 'API_ERROR'}: ${apiError?.message ?? 'Error'}`)
      )
    })
    return
  }

  // 4xx — only report the ones worth monitoring.
  if (status && status >= 400 && shouldReport4xx(status, apiError?.errorCode)) {
    Sentry.withScope((scope) => {
      scope.setLevel('warning')
      if (apiError) {
        scope.setTag('errorCode', apiError.errorCode)
        scope.setExtra('path', apiError.path)
        scope.setExtra('method', apiError.method)
        scope.setExtra('statusCode', apiError.statusCode)
        if (apiError.requestId) scope.setExtra('requestId', apiError.requestId)
      }
      Sentry.captureMessage(
        `API ${status} ${apiError?.errorCode ?? 'unknown'} en ${apiError?.path ?? (isAxiosError(error) ? error.config?.url : '') ?? ''}`,
        'warning'
      )
    })
  }
}

/**
 * Shows the rich error toast (sonner manages it via toast.custom; we own the
 * content). Pass `actions` to make it actionable. UI only — does NOT report to
 * Sentry; callers decide reporting (see handleMutationError / useErrorHandler).
 */
/** Base lifetime for an error toast; longer than sonner's 4s default so the user
 * can read it. Actionable toasts last even longer so there is time to click. */
const ERROR_TOAST_DURATION = 8000
const ERROR_TOAST_DURATION_WITH_ACTIONS = 15000

export function showErrorToast(error: unknown, actions: ErrorAction[] = []): void {
  const { severity, title, message, requestId, fields } = getErrorPresentation(error)
  // createElement (not JSX) keeps this module a plain .ts util while still
  // rendering the rich component through sonner's toast.custom.
  toast.custom(
    (id) =>
      createElement(ErrorToast, {
        severity,
        title,
        message,
        requestId,
        fields,
        actions,
        onDismiss: () => toast.dismiss(id)
      }),
    {
      duration: actions.length > 0 ? ERROR_TOAST_DURATION_WITH_ACTIONS : ERROR_TOAST_DURATION
    }
  )
}

/**
 * Standard mutation error handler (no actions). The global fallback in app.tsx
 * and the default for any mutation without a custom onError. For actionable
 * errors use the `useErrorHandler(registry)` hook instead.
 */
export function handleMutationError(error: unknown): void {
  showErrorToast(error)
  reportErrorToSentry(error)
}

/**
 * Mutation handler for flows where the same request can be safely repeated.
 * The action is offered only for transport and 5xx failures; validation and
 * authentication errors remain non-retryable.
 */
export function handleRetryableMutationError(error: unknown, retry: () => void): void {
  showErrorToast(
    error,
    isRetryableError(error)
      ? [{ label: 'Reintentar', onClick: retry, primary: true, icon: RotateCcw }]
      : []
  )
  reportErrorToSentry(error)
}

function shouldReport4xx(status: number, errorCode?: string): boolean {
  // 401: token expirado / sesión cerrada — pasa todo el tiempo, ruido puro.
  if (status === 401) return false
  // VALIDATION_ERROR del back: lo dispara el form mal completado.
  if (errorCode === 'VALIDATION_ERROR') return false
  return true
}
